import { computed, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe } from 'rxjs';
import { filter, map, startWith, tap } from 'rxjs/operators';
import { navGroupLabelForUrl, visibleNavItems } from './app-nav';
import { withAdminIdentity } from './core/auth/with-admin-identity';
import {
    AdminApiEnvironment,
    AdminApiEnvironmentKind,
    apiEnvironmentLabel,
    apiEnvironmentTone,
} from './core/country/api-environment.model';
import { CountryDomainService } from './core/country/country-domain.service';
import { ALL_COUNTRIES, CountryCode } from './core/country/country.model';
import { withActiveCountry } from './core/country/with-active-country';
import { AdminTheme } from './core/theme/admin-theme';
import { ThemeService } from './core/theme/theme.service';

interface AppShellState {
    isSidenavOpen: boolean;
    currentUrl: string;
    /**
     * Mirrors `CountryDomainService.allowedCountries` — the countries the signed-in staff member's
     * token is assigned to, and so the only ones the toolbar switcher may offer.
     */
    availableCountries: CountryCode[];
    /**
     * Mirrors `CountryDomainService.isProdDeployment()` so deployment-gated nav entries can be filtered
     * from a computed. Held in state rather than read in the computed because a `withComputed` may only
     * read store signals and call pure functions; it is set once in `onInit`, since the value derives
     * from the host name and cannot change without a reload.
     */
    isProdDeployment: boolean;
    /** Mirrors `CountryDomainService.isDynamicEnv()` for the dyn-env-only nav entries; held and set like `isProdDeployment`. */
    isDynamicEnv: boolean;
    /**
     * Mirrors `CountryDomainService.apiEnvironment()` for the toolbar indicator. Held in state for the
     * same reason as `isProdDeployment` above, and set alongside it.
     */
    apiEnvironment: AdminApiEnvironment;
    /**
     * The API origin of the *active* country — the indicator's tooltip. Unlike the two fields above this
     * one does change at runtime, so it is refreshed by `_watchApiBaseUrlRx` on every country switch.
     */
    apiBaseUrl: string;
    /**
     * Mirrors `ThemeService.activeTheme` so the toolbar switch can render the scheme it offers. The
     * *appearance* does not depend on this — the palette is CSS-driven — only the icon and its label.
     */
    theme: AdminTheme;
}

const initialState: AppShellState = {
    isSidenavOpen: true,
    currentUrl: '',
    availableCountries: [...ALL_COUNTRIES],
    isProdDeployment: false,
    isDynamicEnv: false,
    apiEnvironment: { kind: AdminApiEnvironmentKind.Production },
    apiBaseUrl: '',
    theme: AdminTheme.Dark,
};

/**
 * The shell's own state — nav, sidenav, toolbar — provided by `AppComponent` like any other
 * component's store. App-wide facts (who is signed in, which country, which scheme, which API) are
 * owned by the root services and bridged in here; nothing outside the shell injects this store, so
 * a page that needs the role or the country composes the same features this one does.
 */
export const AppShellStore = signalStore(
    withAdminIdentity(),
    withActiveCountry(),
    withState(initialState),
    withComputed((store) => ({
        isAuthenticated: computed(() => store.user() !== null),
        visibleNav: computed(() => visibleNavItems(
            store.role(),
            store.activeCountry(),
            store.isProdDeployment(),
            store.isDynamicEnv(),
        )),
        apiEnvironmentLabel: computed(() => apiEnvironmentLabel(store.apiEnvironment())),
        apiEnvironmentTone: computed(() => apiEnvironmentTone(store.apiEnvironment())),
        /**
         * Name of the signed-in staff member for the toolbar. Empty while signed out, and also for a
         * token carrying no `name` claim — `BffTokenParserService` maps that claim to `''`. The
         * toolbar renders the name only when this is non-empty.
         */
        userName: computed(() => store.user()?.name ?? ''),
        /**
         * A switcher with one option is a label, not a control — staff assigned to a single country
         * get no country picker at all rather than one that cannot change anything.
         */
        canSwitchCountry: computed(() => store.availableCountries().length > 1),
        /**
         * The switch advertises what it will DO rather than what is currently on — in dark it offers
         * the sun — so the icon and its tooltip name the same destination and the control reads as an
         * action instead of a status light.
         */
        themeToggleIcon: computed(() => (store.theme() === AdminTheme.Dark ? 'light_mode' : 'dark_mode')),
        themeToggleLabel: computed(() => (
            store.theme() === AdminTheme.Dark ? 'Switch to light theme' : 'Switch to dark theme'
        )),
    })),
    withComputed((store) => ({
        expandedNavGroupLabel: computed(() => navGroupLabelForUrl(store.visibleNav(), store.currentUrl())),
    })),
    withMethods((
        store,
        countryDomainService = inject(CountryDomainService),
        themeService = inject(ThemeService),
    ) => ({
        /**
         * Flips the scheme. The repaint is the service's business (it writes `<html>`); this only
         * exists so the template has a single store call to bind, and `_watchThemeRx` below is what
         * brings the resulting scheme back into state for the icon.
         */
        toggleTheme(): void {
            themeService.toggle();
        },
        toggleSidenav(): void {
            patchState(store, s => ({ isSidenavOpen: !s.isSidenavOpen }));
        },
        openSidenav(): void {
            patchState(store, { isSidenavOpen: true });
        },
        closeSidenav(): void {
            patchState(store, { isSidenavOpen: false });
        },
        /**
         * Fed the service observable in `onInit`, not `store.activeCountry`, so the tooltip is seeded
         * synchronously like the rest of the toolbar state.
         */
        _watchApiBaseUrlRx: rxMethod<CountryCode>(pipe(
            tap(country => patchState(store, { apiBaseUrl: countryDomainService.apiBaseUrl(country) })),
        )),
        _watchAllowedCountriesRx: rxMethod<CountryCode[]>(pipe(
            tap(availableCountries => patchState(store, { availableCountries })),
        )),
        _watchThemeRx: rxMethod<AdminTheme>(pipe(
            tap(theme => patchState(store, { theme })),
        )),
        _watchUrlRx: rxMethod<string>(pipe(
            tap(currentUrl => patchState(store, { currentUrl })),
        )),
    })),
    withHooks((
        store,
        countryDomainService = inject(CountryDomainService),
        themeService = inject(ThemeService),
        router = inject(Router),
    ) => ({
        onInit: () => {
            // Set before the first render: `AppComponent` providing and injecting this store is what runs this.
            patchState(store, {
                isProdDeployment: countryDomainService.isProdDeployment(),
                isDynamicEnv: countryDomainService.isDynamicEnv(),
                apiEnvironment: countryDomainService.apiEnvironment(),
            });
            // `countryChanges$` is a BehaviorSubject, so this seeds `apiBaseUrl` synchronously.
            store._watchApiBaseUrlRx(countryDomainService.countryChanges$);
            store._watchAllowedCountriesRx(countryDomainService.allowedCountriesChanges$);
            // Also a BehaviorSubject, so this seeds the stored scheme into state for the toolbar icon.
            store._watchThemeRx(themeService.themeChanges$);
            store._watchUrlRx(router.events.pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                map(event => event.urlAfterRedirects),
                startWith(router.url),
            ));
        },
    })),
);
